import { Link, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import api from "@/lib/api";
import { PublicShell, SignupCta, useSeo } from "./glossary/PublicShell";
import { Heart, Map, FolderHeart, Compass, ArrowRight } from "lucide-react";

const FEATURES = [
  { icon: Compass, title: "A clear path forward", body: "A personalized roadmap of funding, services and next steps — in plain language." },
  { icon: Map, title: "Find trusted providers", body: "Search Ontario therapists, clinics and programs in one place." },
  { icon: FolderHeart, title: "Keep it all together", body: "A private space for documents, tasks and the moments that matter." },
];

export default function Welcome() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  useEffect(() => {
    if (!ref) return;
    try { localStorage.setItem("kidpath_ref", ref); } catch {}
    const sentinel = `kidpath_ref_visit_${ref}`;
    try {
      if (sessionStorage.getItem(sentinel)) return;
      sessionStorage.setItem(sentinel, "1");
    } catch {}
    api.post("/referral/visit", { ref }).catch(() => {});
  }, [ref]);
  useSeo({
    title: "A KidPath family shared a moment with you | KidPath",
    description: "Someone thought of you. KidPath helps Canadian families raising neurodivergent children find funding, services and support — with clarity.",
    noindex: true,
  });

  return (
    <PublicShell>
      {/* Warm hero */}
      <section className="relative overflow-hidden bg-kid-navy text-white" data-testid="welcome-hero">
        <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #1FA5A0 0, transparent 40%), radial-gradient(circle at 80% 30%, #5AB4EE 0, transparent 45%)" }} />
        <div className="relative max-w-[1180px] mx-auto px-5 md:px-[30px] py-16 sm:py-24 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-sm font-semibold text-white/90" data-testid="welcome-eyebrow">
            <Heart size={15} className="text-kid-coral fill-kid-coral" /> Someone's thinking of you
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-6 leading-tight" data-testid="welcome-title">
            A KidPath family shared<br className="hidden sm:block" /> a moment with you 💛
          </h1>
          <p className="text-lg text-white/85 max-w-2xl mx-auto mt-5" data-testid="welcome-subtitle">
            Behind that photo is a family navigating the world of autism and neurodiversity support in Canada — and they use <b className="text-white">KidPath</b> to help find their way.
          </p>
          <p className="text-base text-white/70 max-w-2xl mx-auto mt-3">
            If you're raising a neurodivergent child too, KidPath is here to make the path a little clearer — free to start.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/register" className="inline-flex items-center gap-1.5 rounded-full bg-kid-tealcta text-white font-semibold px-7 py-3.5 hover:-translate-y-0.5 transition-transform" data-testid="welcome-cta-primary">
              Start your free support check <ArrowRight size={17} />
            </Link>
            <Link to="/#how" className="inline-flex items-center rounded-full border border-white/30 text-white font-semibold px-7 py-3.5 hover:bg-white/10 transition-colors" data-testid="welcome-cta-secondary">
              See how KidPath works
            </Link>
          </div>
        </div>
      </section>

      {/* What is KidPath */}
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-14 sm:py-20">
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-center text-kid-navy">What families use KidPath for</h2>
        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          {FEATURES.map((f) => (
            <div key={f.title} className="kid-card p-6" data-testid="welcome-feature">
              <div className="w-11 h-11 rounded-2xl bg-kid-teal/10 grid place-items-center text-kid-teal mb-4"><f.icon size={22} /></div>
              <h3 className="font-display font-bold text-kid-navy text-lg">{f.title}</h3>
              <p className="text-kid-ink/80 mt-1.5 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-12"><SignupCta testid="welcome-cta-band" /></div>
        <p className="text-center text-xs text-kid-muted mt-8" data-testid="welcome-ref-note">
          {ref === "journal" ? "You're here because someone shared a journal moment with you." : "Welcome to KidPath."} KidPath provides general information and navigation tools — not medical, legal or funding-eligibility advice.
        </p>
      </section>
    </PublicShell>
  );
}
